import React from 'react';
import { Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { IMyNetworkScoreSummaryResponse } from '../../interfaces/INetworkScore';
import { NetworkScoreDetails } from './NetworkScoreDetails';

interface NetworkScoreCardProps {
    summary: IMyNetworkScoreSummaryResponse;
    /** Shows an "Open" button that takes the user to the full Network score view. */
    onOpen?: () => void;
}

/**
 * Network home — Network Score panel, shown above "Manage my network" (network-score.md, section
 * 8 "Surfaces and moments"); the content lives in {@link NetworkScoreDetails}.
 */
export const NetworkScoreCard: React.FC<NetworkScoreCardProps> = ({ summary, onOpen }) => {
    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, position: 'relative', overflow: 'hidden' }}>

            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2, position: 'relative' }}>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, minWidth: 0 }}>Network Score</Typography>
                {onOpen && (
                    <Button size="small" onClick={onOpen} sx={{ minWidth: 0, flexShrink: 0, whiteSpace: 'nowrap' }}>
                        View score
                    </Button>
                )}
            </Stack>

            <NetworkScoreDetails summary={summary} condensed />
        </Paper>
    );
};

/** Loading placeholder, shown while the summary fetch is in flight. */
export const NetworkScoreCardSkeleton: React.FC = () => (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Skeleton
                variant="circular"
                sx={{ width: { xs: 56, sm: 88, md: 64, lg: 72 }, height: { xs: 56, sm: 88, md: 64, lg: 72 }, flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="40%" height={32} />
                <Skeleton variant="text" width="60%" />
            </Box>
        </Stack>
        <Skeleton variant="rounded" height={8} sx={{ my: 2, borderRadius: 999 }} />
        <Skeleton variant="text" width="45%" sx={{ display: { xs: 'block', sm: 'none' } }} />
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="text" width="80%" />
        </Box>
    </Paper>
);

export default NetworkScoreCard;
